import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportRtFilterOptions } from "../report-rt";
import { ReportRtFilterSettingsForm } from "../settings-form/ReportRtFilterSettingsForm";
import { ReportRtFilterSettings } from "../settings/ReportRtFilterSettings";
import { ReportRtTableDataRequest, TransfertDataRequest } from "../data-request/ReportRtTableDataRequest";
import { ReportRTRow } from "../table-data/ReportRtRow";
import { ReportRtJournalRequest } from "../journal/ReportRtJournalRequest";
import { MutuallyRedeemingLevel } from "../../../common/types/roots/fields";
import { Option } from "../../../common/types/Option";

export class ReportRtFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportRtFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'RT') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'RT') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }
        
        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }



        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let paymentTypeOption = options.paymentTypes.find(it => it.value === settings.paymentType)
        if (!paymentTypeOption) {
            throw new Error('error_not_found_payment_option')
        }
        paymentTypeOption = { ...paymentTypeOption }

        let generateByKbkTypeOption = options.generateByKbkType.find(it => it.value === settings.generateByKbk)
        if (!generateByKbkTypeOption) {
            throw new Error('error_not_found_kbk_option')
        }
        generateByKbkTypeOption = { ...generateByKbkTypeOption }

        let representationTypeOption = options.representationType.find(it => it.value === settings.representation)
        if (!representationTypeOption) {
            throw new Error('error_not_found_representation_option')
        }
        representationTypeOption = { ...representationTypeOption }

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )


        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })


        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }


        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }



        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        // 2. Формирование
        const form: ReportRtFilterSettingsForm = {
            // Base:
            key: 'RT',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            date: new Date(settings.date),
            region: regionOption,
            paymentType: paymentTypeOption,
            generateByKbk: generateByKbkTypeOption,
            representation: representationTypeOption,
            budgetTypes: budgetTypeOptions,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            statuses: statusOptions,
            transferts: cloneDeep(settings.transferts),
            expenses: cloneDeep(settings.expenses),
            measureUnit: measureUnitOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
        }
        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'RT') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_period')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.paymentType) {
            throw new Error('error_input_payment_type ')
        }

        if (!form.generateByKbk) {
            throw new Error('error_input_kbk_report')
        }
        
        if (!form.representation) {
            throw new Error('error_input_representation')
        }
        
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportRtFilterSettings = {
            // Base:
            reportName: 'RT',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            date: strDate,
            region: form.region.value,
            paymentType: form.paymentType?.value ?? null,
            generateByKbk: form.generateByKbk?.value ?? null,
            representation: form.representation?.value,
            budgetTypes: form.budgetTypes.map(it => it.value),
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            statuses: form.statuses.map(it => it.value),
            expenses: cloneDeep(form.expenses),
            transferts: cloneDeep(form.transferts),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportRtFilterOptions)
        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()

        const emptyForm: ReportRtFilterSettingsForm = {
            key: 'RT',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'RT')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'EXPENSES')!,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields    
            date: yesterdayDate,
            region: null,
            paymentType: options.paymentTypes.find(it => it.value === 'PAYMENTS')!,
            generateByKbk: options.generateByKbkType.find(it => it.value === 'OwnLevel')!,
            representation: options.representationType.find(it => it.value === 'PROJECT_REGION')!,
            budgetTypes: [],
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            mutuallyRedeemingLevels: [],
            statuses: options.statuses.filter(it => it.value === '39')!,
            expenses: cloneDeep(options.expenses),
            transferts: cloneDeep(options.transferts),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'RT') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.paymentType) {
            throw new Error('error_input_payment_type ')
        }

        if (!form.representation) {
            throw new Error('error_input_representation')
        }

        if (!form.representation) {
            throw new Error('error_input_representation')
        }

        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }



        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }


        const activeExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        
        const activeTransferts: TransfertDataRequest[] = form.transferts
            .filter(item => item.active)
            .map(it => ({ key: it.key, selected: it.selected.map(s => s.value) }))
    

        const statuses = form.statuses.map(it => Number(it.value))
        if (statuses.length <= 0) {
            throw new Error('error_input_statuses')
        }


        const strDate = moment(form.date).format('YYYY-MM-DD')

        const params: ReportRtTableDataRequest = {
            report_date: strDate,
            region: form.region.value,
            payment_type: form.paymentType.value,
            representation: form.representation?.value,
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            statuses: statuses,
            expenses: activeExpenses,
            transferts: activeTransferts,
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/rt',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }

        const responseTableData: { items: ReportRTRow[] } = await response.json()
        const tableData: ReportRTRow[] = responseTableData.items

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportRtJournalRequest = {
            reportName: "RT",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            classificators: activeExpenses.map(it => it.key),
            transferts: activeTransferts.map(it => it.key),
            report_date: strDate,
            measure_unit: form.measureUnit.value,
            round_up: roundUpTo,
            items: tableData, 
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
